import {
	ArrowLeftOutlined,
	CheckCircleFilled,
	FilePdfOutlined,
	PlayCircleOutlined,
	ReadOutlined,
} from '@ant-design/icons';
import { Button, Divider, Empty, List, Spin, Typography } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { AppRoutes } from 'src/enums/ERoutes';
import ResellerDashboard from '../../ResellerDashboard/ResellerDashboard';
import { ResellerSiderItem } from 'src/enums/IResellerSiderItems';
import './ChapterDetail.scss';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getChapterById, getResellerAcademy } from '@/store/resellerAcademy/selector';
import { useMutation } from '@tanstack/react-query';
import { updateResellerAcademyHistory } from '@/api/reseller';
import { useDispatch } from 'react-redux';
import {
	updateResellerAcademyTutorialBySlug,
	updateResellerAcademyPdfBySlug,
} from '@/store/resellerAcademy/slice';

const ChapterDetail = () => {
	const { Text, Title } = Typography;
	const { chapterId } = useParams();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const chapter = useAppSelector(getChapterById(Number(chapterId)));
	const { loading } = useAppSelector(getResellerAcademy);

	const { mutate: updateProgress } = useMutation({
		mutationKey: ['update-tutorial-progress'],
		mutationFn: updateResellerAcademyHistory,
		onError: (err) => {
			console.log('error: ', err);
		},
	});

	return (
		<ResellerDashboard activeKey={ResellerSiderItem.ACADEMY} showHeader={false}>
			<Spin spinning={loading}>
				<div className="chapter-detail">
					<Button
						icon={<ArrowLeftOutlined />}
						onClick={() => navigate(AppRoutes.RESELLER_ACADEMY)}
						className="chapter-detail__back-button"
					>
						Back to Chapters
					</Button>

					{!chapter && !loading ? (
						<Empty description="Chapter not found" className="chapter-detail__empty">
							<Button type="primary" onClick={() => navigate(AppRoutes.RESELLER_ACADEMY)}>
								Return to Academy
							</Button>
						</Empty>
					) : (
						<>
							<Title level={2}>{chapter?.title}</Title>
							<Text className="chapter-detail__description">{chapter?.description}</Text>

							{chapter?.videos && chapter?.videos?.length < 1 ? null : (
								<List
									className="chapter-detail__list"
									itemLayout="horizontal"
									dataSource={chapter?.videos}
									renderItem={(video) => (
										<List.Item
											className="chapter-detail__list-item"
											onClick={() =>
												navigate(
													AppRoutes.RESELLER_ACADEMY_VIDEO.replace(
														':chapterId',
														chapter?.id?.toString() || '',
													).replace(':videoSlug', video?.slug?.toString() || ''),
												)
											}
										>
											<List.Item.Meta
												avatar={
													video.isCompleted ? (
														<CheckCircleFilled className="chapter-detail__icon chapter-detail__icon--completed" />
													) : (
														<PlayCircleOutlined className="chapter-detail__icon" />
													)
												}
												title={video.title}
												description={
													<div className="chapter-detail__video-meta">
														<Text className="chapter-detail__video-description">
															{video.description}
														</Text>
														<Text className="chapter-detail__video-duration">
															{(video.duration / 60).toPrecision(2)} mins
														</Text>
													</div>
												}
											/>
										</List.Item>
									)}
								/>
							)}

							{chapter?.pdfs && chapter.pdfs.length > 0 && (
								<>
									{chapter.videos?.length ? (
										<>
											<Divider className="chapter-detail__divider" />
											<Title level={3}>Reading Materials</Title>
										</>
									) : null}
									<List
										className="chapter-detail__list chapter-detail__pdf-list"
										itemLayout="horizontal"
										dataSource={chapter.pdfs}
										renderItem={(pdf) => (
											<List.Item
												className="chapter-detail__list-item"
												onClick={() => {
													if (chapter?.videos?.length < 1) {
														// Call the API to update the progress on the server
														updateProgress({
															chapterId: chapter?.id,
															videoSlug: pdf?.slug,
															isCompleted: true,
															watchedDuration: 0,
														});

														// Also update the Redux store to reflect the change immediately
														dispatch(
															updateResellerAcademyPdfBySlug({
																chapterId: chapter?.id,
																slug: pdf?.slug,
																pdf: {
																	...pdf,
																	isCompleted: true,
																},
															}),
														);
													}

													navigate(
														AppRoutes.RESELLER_ACADEMY_PDF.replace(
															':chapterId',
															chapter?.id?.toString() || '',
														).replace(':pdfSlug', pdf?.slug?.toString() || ''),
													);
												}}
											>
												<List.Item.Meta
													avatar={
														pdf.isCompleted ? (
															<CheckCircleFilled className="chapter-detail__icon chapter-detail__icon--completed" />
														) : (
															<FilePdfOutlined className="chapter-detail__icon chapter-detail__icon--pdf" />
														)
													}
													title={pdf.title}
												/>
											</List.Item>
										)}
									/>
								</>
							)}

							{chapter?.tutorials && chapter.tutorials.length > 0 && (
								<>
									{chapter.videos?.length || chapter.pdfs?.length ? (
										<>
											<Divider className="chapter-detail__divider" />
											<Title level={3}>Interactive Tutorials</Title>
										</>
									) : null}
									<List
										className="chapter-detail__list chapter-detail__tutorial-list"
										itemLayout="horizontal"
										dataSource={chapter.tutorials}
										renderItem={(tutorial) => (
											<List.Item
												className="chapter-detail__list-item"
												onClick={() => {
													if (!tutorial.isCompleted) {
														// Call the API to update the progress on the server
														updateProgress({
															chapterId: chapter?.id,
															videoSlug: tutorial?.slug,
															isCompleted: true,
															watchedDuration: 0,
														});

														// Also update the Redux store to reflect the change immediately
														dispatch(
															updateResellerAcademyTutorialBySlug({
																chapterId: chapter?.id,
																slug: tutorial?.slug,
																tutorial: {
																	...tutorial,
																	isCompleted: true,
																},
															}),
														);
													}

													navigate(
														AppRoutes.RESELLER_ACADEMY_TUTORIAL.replace(
															':chapterId',
															chapter?.id?.toString() || '',
														).replace(':tutorialSlug', tutorial?.slug?.toString() || ''),
														{ state: { tutorialUrl: tutorial.key } },
													);
												}}
											>
												<List.Item.Meta
													avatar={
														tutorial.isCompleted ? (
															<CheckCircleFilled className="chapter-detail__icon chapter-detail__icon--completed" />
														) : (
															<ReadOutlined className="chapter-detail__icon chapter-detail__icon--tutorial" />
														)
													}
													title={tutorial.title}
												/>
											</List.Item>
										)}
									/>
								</>
							)}
						</>
					)}
				</div>
			</Spin>
		</ResellerDashboard>
	);
};

export default ChapterDetail;
